/* eslint-disable @next/next/no-img-element */
'use client';

import { useBreakpointHooks } from '@/utils/break-points.provider';
import Button from '@ui/Button';
import Link from '@ui/Link';
import { kebabCase } from 'lodash-es';
import React from 'react';
import { MdOutlineChevronLeft, MdOutlineChevronRight } from 'react-icons/md';
import { useSpringCarousel } from 'react-spring-carousel';
import Avatar from '@ui/Avatar';

function SupportedNonprofitsCarousel(props) {
  const { nonProfits } = props;
  const isExtraSmallScreen = useBreakpointHooks((bp) => bp.useSmaller('sm'));
  const isSmallScreen = useBreakpointHooks((bp) => bp.useSmaller('md'));
  const isMediumScreen = useBreakpointHooks((bp) => bp.useSmaller('lg'));
  const isLargeScreen = useBreakpointHooks((bp) => bp.useSmaller('xl'));
  const isExtraLargeScreen =
    !isSmallScreen && !isMediumScreen && !isLargeScreen;
  let itemsPerSlide = isSmallScreen
    ? 4
    : isMediumScreen
    ? 7
    : isLargeScreen
    ? 6
    : 7;

  // const carouselItems = [...nonProfits];
  // if (carouselItems.length < itemsPerSlide) {
  //   const emptyItems = Array(itemsPerSlide - carouselItems.length)
  //     .fill()
  //     .map((i, index) => ({ id: `empty-${index}`, empty: true }));
  //   carouselItems.push(...emptyItems);
  // }

  const carouselItems = [...nonProfits];
  const emptySlotCount = Math.ceil(itemsPerSlide - carouselItems.length);
  if (emptySlotCount > 0) {
    const emptyItems = Array(emptySlotCount)
      .fill()
      .map((_, index) => ({ id: `empty-${index}`, empty: true }));
    carouselItems.push(...emptyItems);
  }

  const renderPostImage = (nonProfit) => {
    return (
      <Avatar
        src={nonProfit?.logo_url || ''}
        alt={nonProfit?.name || `nonProfit-${nonProfit.id}`}
        size="mdlg"
        className="border-2 border-primary bg-white"
        profile="npo"
      />
    );
  };

  const renderItem = (nonProfit) => {
    if (nonProfit.empty) return <div key={nonProfit.id} className="h-full w-full" />;

    const slug = nonProfit?.slug
      ? `/${nonProfit.slug}`
      : `/nonprofit/${kebabCase(nonProfit.name)}-${nonProfit.id}`;

    return (
      <Link
        href={slug}
        className="flex w-[75px] flex-col items-center justify-center lg:w-[110px]">
        {renderPostImage(nonProfit)}
        <div className="mb-2 mt-2 line-clamp-1 text-center text-12 font-medium text-text-primary hover:text-primary lg:text-14">
          {nonProfit?.name}
        </div>
      </Link>
    );
  };

  const canNavigate = nonProfits.length >= itemsPerSlide;

  const {
    carouselFragment,
    slideToPrevItem,
    slideToNextItem,
    getCurrentActiveItem,
  } = useSpringCarousel({
    slideAmount: isMediumScreen ? 75 : 110,
    withLoop: canNavigate,
    itemsPerSlide: itemsPerSlide,
    gutter: isSmallScreen ? 0 : 25,
    items: carouselItems.map((np) => ({
      id: np.id,
      renderItem: renderItem(np),
    })),
  });

  const handleNextClick = () => {
    slideToNextItem();
  };

  const handlePrevClick = () => {
    slideToPrevItem();
  };

  return (
    <div className="group w-full overflow-hidden">
      <div className="relative flex w-[calc(100%+1rem)] items-center justify-center gap-2 lg:gap-11">
        <div className="flex-1 gap-3 overflow-hidden">{carouselFragment}</div>
        <div className="top-2/5 absolute left-2">
          <Button
            variant="icon"
            size="xs"
            className="bg-body-background opacity-90 group-hover:bg-body-background"
            onClick={handlePrevClick}
            disabled={!canNavigate}>
            <MdOutlineChevronLeft
              className={`h-7 w-7 opacity-100 ${
                canNavigate ? 'text-text-disabled' : 'text-body-background'
              }`}
            />
          </Button>
        </div>
        <div className="top-2/5 absolute right-6">
          <Button
            variant="icon"
            size="xs"
            className="bg-body-background opacity-90 group-hover:bg-body-background"
            onClick={handleNextClick}
            disabled={!canNavigate}>
            <MdOutlineChevronRight
              className={`h-7 w-7 opacity-100 ${
                canNavigate ? 'text-text-disabled' : 'text-body-background'
              }`}
            />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default React.memo(SupportedNonprofitsCarousel);
